// CharityCategoryManagerModal.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";

export default function CharityCategoryManagerModal({
                                                        initialCategories = [],
                                                        onClose,
                                                        onUpdated, // (newCategories) => void
                                                    }) {
    const [rows, setRows] = useState(initialCategories || []);
    const [loading, setLoading] = useState(!initialCategories.length);
    const [savingId, setSavingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const [newName, setNewName] = useState("");
    const [newDescription, setNewDescription] = useState("");

    // Ensure we are up to date from server on first open
    useEffect(() => {
        const fetchCats = async () => {
            try {
                const { data } = await axios.get(
                    "/api/resources/charity-categories"
                );
                setRows(data || []);
            } catch (err) {
                console.error("Failed to load categories:", err);
                alert("Failed to load categories.");
            } finally {
                setLoading(false);
            }
        };

        if (!initialCategories.length) {
            fetchCats();
        } else {
            setLoading(false);
        }
    }, [initialCategories]);

    // Sync up to parent whenever rows change
    useEffect(() => {
        onUpdated && onUpdated(rows);
    }, [rows, onUpdated]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newName.trim()) {
            alert("Name is required.");
            return;
        }

        try {
            const { data } = await axios.post(
                "/api/resources/charity-categories",
                {
                    name: newName.trim(),
                    description: newDescription.trim() || null,
                }
            );
            setRows((prev) => [...prev, data]);
            setNewName("");
            setNewDescription("");
        } catch (err) {
            console.error("Error creating category:", err);
            const msg =
                err.response?.data?.error || "Failed to create category.";
            alert(msg);
        }
    };

    const handleSaveRow = async (row) => {
        if (!row.name || !row.name.trim()) {
            alert("Name is required.");
            return;
        }
        setSavingId(row.id);
        try {
            const { data } = await axios.put(
                `/api/resources/charity-categories/${row.id}`,
                {
                    name: row.name.trim(),
                    description: row.description?.trim() || null,
                }
            );
            setRows((prev) =>
                prev.map((r) => (r.id === row.id ? data : r))
            );
        } catch (err) {
            console.error("Error updating category:", err);
            const msg =
                err.response?.data?.error || "Failed to update category.";
            alert(msg);
        } finally {
            setSavingId(null);
        }
    };

    const handleDeleteRow = async (row) => {
        if (
            !window.confirm(
                `Delete category "${row.name}"? This may affect charities using it.`
            )
        ) {
            return;
        }
        setDeletingId(row.id);
        try {
            await axios.delete(
                `/api/resources/charity-categories/${row.id}`
            );
            setRows((prev) => prev.filter((r) => r.id !== row.id));
        } catch (err) {
            console.error("Error deleting category:", err);
            const msg =
                err.response?.data?.error || "Failed to delete category.";
            alert(msg);
        } finally {
            setDeletingId(null);
        }
    };

    const updateRowField = (id, field, value) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === id ? { ...r, [field]: value } : r
            )
        );
    };

    return (
        <div
            className="cfm-overlay"
            onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <div className="cfm-dialog">
                <header className="cfm-header">
                    <h2>Manage Charity Types</h2>
                    <button className="cfm-close" onClick={onClose}>
                        ×
                    </button>
                </header>

                <div className="cfm-body">
                    {loading ? (
                        <p className="muted">Loading types…</p>
                    ) : (
                        <>
                            <div className="cfm-grid span-2" style={{ gridTemplateColumns: "1fr" }}>
                                <p className="muted">
                                    These types are used to classify charities, funds,
                                    Masonic bodies, and events (e.g. TIMC, race days, benevolence funds).
                                </p>
                            </div>

                            {/* Existing types */}
                            {rows.length === 0 ? (
                                <p className="muted">No types defined yet.</p>
                            ) : (
                                <div className="cm-category-table">
                                    {rows.map((row) => (
                                        <div
                                            key={row.id}
                                            className="cm-category-row"
                                        >
                                            <div className="cm-category-main">
                                                <label>
                                                    <span>Name</span>
                                                    <input
                                                        value={row.name || ""}
                                                        onChange={(e) =>
                                                            updateRowField(
                                                                row.id,
                                                                "name",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </label>
                                                <label>
                                                    <span>Description</span>
                                                    <input
                                                        value={row.description || ""}
                                                        onChange={(e) =>
                                                            updateRowField(
                                                                row.id,
                                                                "description",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </label>
                                            </div>
                                            <div className="cm-category-actions">
                                                <button
                                                    className="btn btn-sm"
                                                    disabled={savingId === row.id}
                                                    onClick={() => handleSaveRow(row)}
                                                >
                                                    {savingId === row.id ? "Saving…" : "Save"}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    disabled={deletingId === row.id}
                                                    onClick={() => handleDeleteRow(row)}
                                                >
                                                    {deletingId === row.id ? "Deleting…" : "Delete"}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add new type */}
                            <hr style={{ margin: "12px 0", borderColor: "#e5e7eb" }} />
                            <form className="cfm-grid" onSubmit={handleAdd}>
                                <label>
                                    <span>New type name*</span>
                                    <input
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder="e.g. Masonic Charity, Event / Fundraiser"
                                    />
                                </label>
                                <label>
                                    <span>Description</span>
                                    <input
                                        value={newDescription}
                                        onChange={(e) => setNewDescription(e.target.value)}
                                        placeholder="Optional description"
                                    />
                                </label>
                                <div className="span-2" style={{ textAlign: "right" }}>
                                    <button type="submit" className="btn btn-primary">
                                        Add Type
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>

                <footer className="cfm-footer">
                    <button className="btn btn-ghost" onClick={onClose}>
                        Close
                    </button>
                </footer>
            </div>
        </div>
    );
}